import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import React, { FC, FunctionComponent } from 'react'
import { T, useT } from '@transifex/react'
import { useFormatCurrencyValue } from '../../helpers/formatNumberHooks'

export type PartialPaymentTypeName = 'Store Credit' | 'Loyalty'

interface Props {
  availableCredit: number
  paymentTypeName: PartialPaymentTypeName
  close: (applyPartialPayment: Promise<boolean>) => void
}

const HeaderContent: FunctionComponent<{
  paymentTypeName: PartialPaymentTypeName
  amount: string
}> = ({ paymentTypeName, amount }) => {
  switch (paymentTypeName) {
    case 'Store Credit':
      return (
        <T
          _str="Pay {amount} with Store Credit"
          amount={amount}
          _context="Payment modal header"
        />
      )
    case 'Loyalty':
      return (
        <T
          _str="Pay {amount} with Loyalty"
          amount={amount}
          _context="Payment modal header"
        />
      )
  }
}

export const PartialPaymentModal: FC<Props> = ({
  availableCredit,
  paymentTypeName,
  close,
}) => {
  const t = useT()
  const formatCurrency = useFormatCurrencyValue()

  const cancelPartialPayment = () => {
    close(Promise.resolve(false))
  }

  const makePartialPayment = () => {
    close(Promise.resolve(true))
  }

  return (
    <Modal size="small" dismissable onClose={cancelPartialPayment}>
      <ModalHeader>
        <HeaderContent
          paymentTypeName={paymentTypeName}
          amount={formatCurrency(availableCredit)}
        />
      </ModalHeader>
      <ModalContent>
        <p className="vd-p">
          {t(
            'You can only redeem up to the value of your current {paymentTypeName} balance. You may still continue with this as a partial payment, then choose another payment method for the remaining balance.',
            {
              paymentTypeName: paymentTypeName.toLowerCase(),
              _context: 'Partial payment modal content',
            }
          )}
        </p>
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={cancelPartialPayment}
            data-track="choose-another-payment-type-partial-payment-modal"
          >
            {t('Choose a different payment type', {
              _context: 'Button label to choose a different payment type',
            })}
          </Button>
          <Button
            variant="go"
            onClick={makePartialPayment}
            autoFocus
            data-track="make-partial-payment"
          >
            {t('Make partial payment', {
              _context: 'Button label to make a partial payment',
            })}
          </Button>
        </div>
      </ModalActions>
    </Modal>
  )
}
